import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AppBar, Box, IconButton, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useColorMode } from '../theme/colorModeContext';
import { useAuth } from '../context/authContext';
import { NotificationBell } from '../components/NotificationBell';
import { NotificationToastHost } from '../components/NotificationToastHost';
import { RouteNotice } from '../components/RouteNotice';
import { Logo } from '../components/Logo';
import { GlobalSearch } from '../components/GlobalSearch';
import { QuickCreateButton } from '../components/QuickCreateButton';
import { BranchSelector } from '../components/BranchSelector';
import { Sidebar } from './Sidebar';
import { useNotificationSummary } from '../hooks/useNotificationSummary';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'imc-sidebar-collapsed';

export const AppLayout = () => {
  const { mode, toggleColorMode } = useColorMode();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // The app's one notification poll (every 60s while this tab is visible);
  // the bell, sidebar badges and toasts all read its shared result.
  useNotificationSummary({ poll: true });

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1',
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      return next;
    });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isDesktop ? (
        <Sidebar variant="permanent" collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      ) : (
        <Sidebar
          variant="temporary"
          collapsed={false}
          onToggleCollapsed={toggleCollapsed}
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{ bgcolor: 'background.paper' }}
        >
          <Toolbar sx={{ gap: 1.5 }}>
            {!isDesktop && (
              <>
                <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="open navigation">
                  <MenuIcon />
                </IconButton>
                <Logo height={28} />
              </>
            )}
            {isDesktop && <GlobalSearch />}
            <Box sx={{ flexGrow: 1 }} />
            {isDesktop && <BranchSelector />}
            <QuickCreateButton />
            <IconButton color="inherit" onClick={toggleColorMode} aria-label="toggle color mode">
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            {hasPermission('notifications', 'view') && <NotificationBell />}
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' }}>
          <Outlet />
        </Box>
      </Box>

      {hasPermission('notifications', 'view') && <NotificationToastHost />}
      <RouteNotice />
    </Box>
  );
};
